import constants from "../constants";
import Service from "../services/base";
import { getCurrentVehicle } from "../utils/device";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { getLastPosition, getCurrentPosition } from "../utils/location";
import { log, error as logError, Source } from "../utils/logger";
import { calculateHeadingDifference } from "../utils/helpers";
import { CancelablePromise } from "../utils/cancelablePromise";
import Api from "./api";
import { DriveStore } from "../store/reducers";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { getServerDate } from "../utils/storage/date";
import DutyStatusLog, { ALL_STANDARD_STATUSES } from "../model/DutyStatusLog";
import LatLon from "../utils/latlon";

interface IProximityInfo {
    position: GeolocationPosition;
}

export interface IAssignmentProximityInfo extends IProximityInfo {
    statuses: any[];
    closeStatuses: any[];
    radius: number;
}

interface IDeviceStatusResult {
    isDeviceCommunicating: boolean;
    latitude: number;
    longitude: number;
}

export interface IUnassignmentProximityInfo extends IProximityInfo {
    distanceFromLastOn: number | null;
    distanceFromVehicle: number | null;
    elapsedTimeFromLastOn: number | null;
    lastLog: DutyStatusLog | null;
    shouldUnassign: boolean;
}

export default class VehicleProximity extends Service {
    private confidenceRadiusKm = 0;

    constructor(
        private readonly api: Api,
        private readonly store: DriveStore,
        private readonly dutyStatusLogService: DutyStatusLogService
    ) {
        super();
    }

    async check() {
        let apiUser = this.api.getUser();
        let currentVehicle = getCurrentVehicle();

        if (this.store.getState().data.ELDJurisdiction !== "EWD") {
            // Vehicle un-assignment check
            if (window.DRIVE_DEBUG && !window.DRIVE_DEBUG.DISABLE_AUTO_UNASSIGN) {
                await this.getUnassignmentStatus(currentVehicle)
                    .then(results => {
                        if (results.shouldUnassign) {
                            dispatcher.trigger(DispatcherEvents.VehicleOutOfRange, apiUser ? apiUser.name : "");
                            log(Source.GEO, `UNASSIGN: Unassignment suggested.` +
                                ` Distance from last ON: ${ results.distanceFromLastOn },` +
                                ` Elapsed time from last ON: ${ results.elapsedTimeFromLastOn },` +
                                ` Distance from vehicle: ${ results.distanceFromVehicle }`);
                        }
                    })
                    .catch((errorObject: Error) => {
                        errorObject && log(Source.GEO, `UNASSIGN: ${ errorObject.message }`);
                    });
            }
        }
    }

    getAssignmentInfo(closestAssetLimit = 10, includeStoppedDevices = true, useLastPosition = true): CancelablePromise<IAssignmentProximityInfo> {
        let positionPromise: CancelablePromise<GeolocationPosition>;
        if (useLastPosition) {
            positionPromise = getLastPosition(this.store);
        } else {
            positionPromise = getCurrentPosition(this.store, {timeout: 5000}).catch(() => {
                return getLastPosition(this.store);
            });
        }
        return positionPromise.then(position => {
            return this.api.callPromise("Get", {
                typeName: "DeviceStatusInfo",
                search: {
                    closestAssetLimit: closestAssetLimit,
                    position: {
                        x: position.coords.longitude,
                        y: position.coords.latitude
                    },
                    deviceSearch: {
                        groups: [{id: "GroupVehicleId" }]
                    }
                },
                propertySelector: {
                    fields: ["device", "latitude", "longitude", "driver", "bearing", "speed"],
                    isIncluded: true
                }
            }).then(statuses => {
                return {
                    statuses: statuses,
                    position: position,
                    closeStatuses: this.findCloseVehicleStatuses(position, statuses, includeStoppedDevices),
                    radius: this.getAssignmentConfidenceRadiusKm(position)
                } as IAssignmentProximityInfo;
            }).catch((errorObject) => {
                logError(Source.GEO, "PROX: Could not call DeviceStatusInfo");
                throw new Error(errorObject);
            });
        });
    }

    getUnassignmentStatus(currentVehicle?: IIdEntity | null): CancelablePromise<IUnassignmentProximityInfo> {
        let vehicle = currentVehicle || getCurrentVehicle();

        if (!vehicle || vehicle.id === "NoDeviceId") {
            return CancelablePromise.reject();
        }

        let distanceFromLastOn: number | null = null;
        let elapsedTimeFromLastOn: number | null = null;
        let lastLog: DutyStatusLog | null = null;
        let shouldUnassign = false;

        return getLastPosition(this.store).then(position => {
            if (this.isMobileDeviceStoppedOrMovingSlow(position) && this.isMobileDeviceAccurate(position)) {
                // Logs to check -- any standard status, sorted reverse chronologically.
                let logCollection = this.dutyStatusLogService.getHOSLogs(true, undefined, true).filter(l => ALL_STANDARD_STATUSES.indexOf(l.status) !== -1);

                // Get the index of the last ON log. Save the log to lastOnLog.
                let indexOfLastOn: Number | null = null;
                let lastOnLog: DutyStatusLog | null = null;
                for (let i = 0; i < logCollection.length; i++) {
                    let currentLog = logCollection[i];
                    if (currentLog.status === "ON") {
                        indexOfLastOn = i;
                        lastOnLog = currentLog;
                        break;
                    }
                }

                lastLog = logCollection.length ? logCollection[0] : null;
                let currentDate = getServerDate();
                if (lastOnLog && lastOnLog.location && lastOnLog.location.location) {
                    let logPosition = lastOnLog.location.location;

                    // Check the relative distances here
                    let deviceLatLon = new LatLon(position.coords.latitude, position.coords.longitude);
                    let logLatLon = new LatLon(logPosition.y, logPosition.x);
                    distanceFromLastOn = deviceLatLon.distanceTo(logLatLon); // km

                    let lastOnTime = lastOnLog.getDate().getTime();
                    let currentDateTime = currentDate.getTime();

                    // Elapsed time
                    elapsedTimeFromLastOn = currentDateTime - lastOnTime;

                    // Check if current time is acceptable range of the last auto ON
                    let inDateRange = elapsedTimeFromLastOn > constants.DEVICE_UNASSIGN.MIN_PERIOD_AFTER_ON_LOG &&
                                      elapsedTimeFromLastOn < constants.DEVICE_UNASSIGN.MAX_PERIOD_AFTER_ON_LOG;

                    shouldUnassign = indexOfLastOn === 0 && inDateRange && distanceFromLastOn >= constants.DEVICE_UNASSIGN.RADIUS;
                }
            }

            let distanceFromVehicle: number | null = null;

            if (!shouldUnassign || !vehicle) {
                return {
                    position, lastLog, distanceFromLastOn, distanceFromVehicle,
                    elapsedTimeFromLastOn, shouldUnassign
                };
            }

            // Cross-check against the GO device's live position. Suppress if
            // the tablet is actually near the vehicle (false positive from stale
            // ON log). Inlined instead of getDeviceStatus() to avoid a redundant
            // getLastPosition() call -- we already have `position`.
            return this.api.callPromise("Get", {
                typeName: "DeviceStatusInfo",
                search: { deviceSearch: { id: vehicle.id } },
                propertySelector: {
                    fields: ["isDeviceCommunicating", "latitude", "longitude"],
                    isIncluded: true
                }
            }).then((statuses: IDeviceStatusResult[]) => {
                if (!statuses || statuses.length === 0) {
                    log(Source.GEO, "UNASSIGN: DeviceStatusInfo returned empty -- skipping live position check");
                } else {
                    let status = statuses[0];
                    if (!status.isDeviceCommunicating) {
                        shouldUnassign = false;
                        log(Source.GEO, "UNASSIGN: Suppressed -- device not communicating; GO position is stale");
                    } else if (!Number.isFinite(status.latitude) || !Number.isFinite(status.longitude)
                            || (status.latitude === 0 && status.longitude === 0)) {
                        log(Source.GEO, "UNASSIGN: Device communicating but no valid position -- skipping live position check");
                    } else {
                        const tabletLatLon = new LatLon(position.coords.latitude, position.coords.longitude);
                        const vehicleLatLon = new LatLon(status.latitude, status.longitude);
                        distanceFromVehicle = tabletLatLon.distanceTo(vehicleLatLon);
                        if (distanceFromVehicle < constants.DEVICE_UNASSIGN.RADIUS) {
                            shouldUnassign = false;
                            log(Source.GEO, `UNASSIGN: Suppressed -- tablet near vehicle (${ distanceFromVehicle } km)`);
                        }
                    }
                }
                return {
                    position, lastLog, distanceFromLastOn, distanceFromVehicle,
                    elapsedTimeFromLastOn, shouldUnassign
                };
            }).catch((err) => {
                log(Source.GEO, `UNASSIGN: DeviceStatusInfo call failed -- ${ err?.message || "unknown" }; preserving trigger`);
                return {
                    position, lastLog, distanceFromLastOn, distanceFromVehicle,
                    elapsedTimeFromLastOn, shouldUnassign
                };
            });
        });
    }

    getDeviceStatus(deviceId: string): CancelablePromise {
        if (!deviceId) {
            throw new Error("Device ID not provided");
        }
        return getLastPosition(this.store)
            .then(position => {
                return this.api.callPromise("Get", {
                    typeName: "DeviceStatusInfo",
                    search: {
                        deviceSearch: { id: deviceId }
                    },
                    propertySelector: {
                        fields: ["exceptionEvents", "statusData"],
                        isIncluded: false
                    }
                })
                .then((statuses: any[]) => {
                    if (!statuses || statuses.length === 0) {
                        throw new Error("No device statuses");
                    }
                    let status = statuses[0];
                    status.distance = new LatLon(position.coords.latitude, position.coords.longitude).distanceTo(new LatLon(status.latitude, status.longitude));
                    return status;
                });
            });
    }

    destroy() {}

    private getAssignmentConfidenceRadiusKm(position: GeolocationPosition): number {
        let coords = position.coords;
        let minRadius = constants.DEVICE_ASSIGN.MIN_RADIUS;
        let maxRadius = constants.DEVICE_ASSIGN.MAX_RADIUS;

        if (!this.isMobileDeviceStoppedOrMovingSlow(position)) {
            // Add additional leeway to the radii if the current device is moving in case the server is delayed by X seconds
            let additionalRadius = (coords.speed || 0) * constants.DEVICE_ASSIGN.MOVING_THRESHOLD_MAX_SERVER_DELAY_SECS;
            minRadius += additionalRadius;
            maxRadius += additionalRadius;
        }

        return Math.min(Math.max(minRadius, position.coords.accuracy), maxRadius) / 1000;
    }

    private isMobileDeviceStoppedOrMovingSlow(currentPosition: GeolocationPosition): boolean {
        let currentKmh = currentPosition && currentPosition.coords.speed ? currentPosition.coords.speed * 3.6 : 0;
        return !!(!currentKmh || (currentKmh && currentKmh < constants.DEVICE_ASSIGN.MOVING_THRESHOLD_SPEED));
    }

    private isMobileDeviceAccurate(currentPosition: GeolocationPosition): boolean {
        let accuracy = currentPosition && currentPosition.coords.accuracy ? currentPosition.coords.accuracy : null;
        return !!(!accuracy || (accuracy && accuracy <= constants.DEVICE_UNASSIGN.MIN_ACCURACY));
    }

    private findCloseVehicleStatuses(currentPosition: GeolocationPosition, statusesToCheck: any[], includeStoppedDevices: boolean): any[] {
        let currentCoord = new LatLon(currentPosition.coords.latitude, currentPosition.coords.longitude);
        this.confidenceRadiusKm = this.getAssignmentConfidenceRadiusKm(currentPosition);
        let innerRadius = Math.min(this.confidenceRadiusKm / 2, constants.DEVICE_ASSIGN.MAX_RADIUS / 1000);
        let filterCandidateStatuses = (candidateStatuses: any[]): any[] => {
            // No vehicles in range
            if (candidateStatuses.length === 0) {
                log(Source.GEO, "PROX: No vehicles in range");
                return [];
            }

            let vehicle = candidateStatuses[0].device;
            let isOnlyStatusInsideRadius = (statuses: any[]): boolean => {
                if (statuses.length < 2) {
                    return statuses.length === 1;
                }
                let distToNextVehicle = statuses[0].distance - statuses[1].distance;
                return distToNextVehicle >= innerRadius;
            };

            // One vehicle in our confidence radius
            if (candidateStatuses.length === 1) {
                log(Source.GEO, `PROX: Found vehicle (id:${ vehicle.id })`);
                return candidateStatuses;
            }

            // Many results (still!), try seeing if the top candidate is in an inner radius
            if (isOnlyStatusInsideRadius(candidateStatuses)) {
                log(Source.GEO, `PROX: Found vehicle in inner radius (id:${ vehicle.id }, inner radius:${ innerRadius.toFixed(1) }km)`);
                return [candidateStatuses[0]];
            }

            // Maybe one of the close vehicles in the radius doesn't have any driver assigned?
            let unassignedStatuses = candidateStatuses.filter(status => !status.driver || status.driver === "UnknownDriverId");
            if (isOnlyStatusInsideRadius(unassignedStatuses)) {
                log(Source.GEO, `PROX: Found unassigned vehicle in inner radius (id:${ vehicle.id }, inner radius:${ innerRadius.toFixed(1) }km)`);
                return [unassignedStatuses[0]];
            }
            log(Source.GEO, "PROX: Too many vehicles in range.");
            return [];
        };

        statusesToCheck.forEach(status => {
            status.distance = currentCoord.distanceTo(new LatLon(status.latitude, status.longitude));
        });

        statusesToCheck.sort((s1, s2) => {
            return s1.distance - s2.distance;
        });

        // Device is stationary, check stationary vehicles
        if (this.isMobileDeviceStoppedOrMovingSlow(currentPosition)) {
            if (!includeStoppedDevices) {
                return [];
            }
            let candidateStatuses = statusesToCheck.filter(status => status.distance <= this.confidenceRadiusKm);
            return filterCandidateStatuses(candidateStatuses);
        }

        // Get moving vehicles if the device is moving, and ones that have a close heading, within a degree of error
        let movingStatuses = statusesToCheck.filter(status => {
            let headingDiff = calculateHeadingDifference(currentPosition.coords.heading, status.bearing);

            return status.distance <= this.confidenceRadiusKm
                    && status.bearing > 0 // -1 means "unknown bearing"
                    && status.speed >= constants.DEVICE_ASSIGN.MOVING_THRESHOLD_SPEED
                    && (!!headingDiff)
                    && headingDiff < constants.DEVICE_ASSIGN.MOVING_THRESHOLD_ANGLE;
        });
        return filterCandidateStatuses(movingStatuses);
    }
}